package com.zi.support.web

import com.zi.support.shared.TemplateHtml
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLTextAreaElement

// More > Templates > New template / Edit: a full-screen builder for a report template. Visual mode is GrapesJS
// (drag-and-drop elements, styles, layers), loaded from jsDelivr on first use; Code mode is the raw HTML. Saving
// calls saveReportTemplate, which publishes it as the next version (or v1 of a new report). Web only.
internal object TemplateBuilder {
    /** What the builder opens: [fromVersion] is the version being edited (null for a new report), [newest] the report's highest. */
    class Draft(val company: String, val companyName: String, val reportId: String, val fromVersion: Int?, val newest: Int,
                val name: String, val description: String, val html: String, val output: dynamic, val sample: dynamic)

    private const val GRAPES = "https://cdn.jsdelivr.net/npm/grapesjs@0.21.13/dist"

    private lateinit var draft: Draft
    private var editor: dynamic = null
    private var parts = TemplateHtml.Parts("", "", "", "")
    private var visual = true
    private var dirty = false
    private var sample: dynamic = null
    private var output: dynamic = null
    private var saved: (String) -> Unit = {}

    fun open(draft: Draft, onSaved: (company: String) -> Unit) {
        this.draft = draft
        saved = onSaved
        sample = draft.sample ?: js("({})")
        output = js("({})")
        output.format = field(draft.output, "format").ifBlank { "A4" }
        output.orientation = field(draft.output, "orientation").ifBlank { "portrait" }
        output.pageNumbers = draft.output?.pageNumbers == true
        output.letterhead = field(draft.output, "letterhead")
        parts = TemplateHtml.split(draft.html)
        dirty = false
        val next = draft.newest + 1
        val editing = draft.fromVersion?.let { "editing v$it · saves as v$next" } ?: "new template · saves as v1"
        element("tb-overlay")?.remove()
        document.body?.insertAdjacentHTML("beforeend", """
          <div id="tb-overlay" class="tb-overlay" role="dialog" aria-modal="true" aria-labelledby="tb-name">
            <header class="tb-bar">
              <button id="tb-close" class="btn btn-icon btn-ghost" aria-label="Close builder"><span class="material-symbols-rounded">close</span></button>
              <div class="tb-title">
                <label class="visually-hidden" for="tb-name">Template name</label>
                <input id="tb-name" class="input" value="${escape(draft.name)}" placeholder="Template name">
                <small>${escape(draft.companyName)} · <code>${escape(draft.reportId)}</code> · $editing</small>
              </div>
              <div class="segmented" role="tablist" aria-label="Editor">
                <button id="tb-mode-visual" role="tab" class="active">Visual</button><button id="tb-mode-code" role="tab">Code</button>
              </div>
              <span class="tb-actions">
                <button id="tb-page" class="btn btn-sm"><span class="material-symbols-rounded" aria-hidden="true">description</span> Page</button>
                <button id="tb-sample" class="btn btn-sm"><span class="material-symbols-rounded" aria-hidden="true">data_object</span> Sample data</button>
                <button id="tb-preview" class="btn btn-sm"><span class="material-symbols-rounded" aria-hidden="true">visibility</span> Preview</button>
                <button id="tb-save" class="btn btn-sm btn-primary"><span class="material-symbols-rounded" aria-hidden="true">save</span> Save v$next</button>
              </span>
            </header>
            <div class="tb-stage">
              <div id="tb-gjs" class="tb-gjs"><div class="empty">Loading the builder…</div></div>
              <textarea id="tb-code" class="tb-code" spellcheck="false" hidden aria-label="Template HTML"></textarea>
            </div>
          </div>
        """.trimIndent())
        document.body?.classList?.add("tb-open")
        element("tb-name")?.addEventListener("input", { dirty = true })
        element("tb-code")?.addEventListener("input", { dirty = true })
        click("tb-close") { close() }
        click("tb-mode-visual") { showVisual() }
        click("tb-mode-code") { showCode() }
        click("tb-page") { pageSettings() }
        click("tb-sample") { sampleData() }
        click("tb-preview") { preview() }
        click("tb-save") { save() }
        val blocker = TemplateHtml.visualBlocker(parts.body)
        if (blocker != null) { showCode(); toast(blocker) } else loadGrapes { startEditor() }
    }

    // ---- GrapesJS ----

    private fun loadGrapes(ready: () -> Unit) {
        if (window.asDynamic().grapesjs != null) { ready(); return }
        if (document.getElementById("grapes-css") == null) document.head?.insertAdjacentHTML("beforeend",
            "<link id=\"grapes-css\" rel=\"stylesheet\" href=\"$GRAPES/css/grapes.min.css\">")
        val script = document.createElement("script").asDynamic()
        script.src = "$GRAPES/grapes.min.js"
        script.onload = { ready() }
        script.onerror = { element("tb-gjs")?.innerHTML = "<div class=\"empty\">The builder couldn't load. Check your connection, or use Code.</div>" }
        document.head?.appendChild(script)
    }

    private fun startEditor() {
        if (element("tb-overlay") == null) return
        element("tb-gjs")?.innerHTML = ""
        val config: dynamic = js("({})")
        config.container = "#tb-gjs"; config.height = "100%"; config.width = "auto"; config.fromElement = false
        config.storageManager = false; config.protectedCss = ""; config.noticeOnUnload = false
        config.components = TemplateHtml.protect(parts.body)
        config.deviceManager = json("devices" to arrayOf(json("id" to "page", "name" to "Page", "width" to "${pageWidth()}px")))
        config.assetManager = json("embedAsBase64" to true)
        config.blockManager = json("blocks" to blocks())
        editor = window.asDynamic().grapesjs.init(config)
        editor.on("load", {
            editor.setDevice("page")
            canvasCss()
            paintStyles()
            dataBlocks()
            editor.Panels.getButton("views", "open-blocks")?.set("active", true)
        })
        editor.on("update", { dirty = true; paintStyles() })
        editor.on("component:add", { paintStyles() })
        visual = true
    }

    /** The template's own CSS and the builder's element CSS, shown in the canvas but kept out of the editor's CSS. */
    private fun canvasCss() {
        val doc = editor?.Canvas?.getDocument() ?: return
        var style = doc.getElementById("tb-template-css")
        if (style == null) { style = doc.createElement("style"); style.id = "tb-template-css"; doc.head.appendChild(style) }
        style.textContent = parts.css + "\n" + TemplateHtml.BLOCK_CSS + "\nbody { padding: 15mm; min-height: 100vh; background: #fff; }"
    }

    /** Inline styles are kept in data-tb-style while editing (TemplateHtml.protect); show the ones without data. */
    private fun paintStyles() {
        val nodes = editor?.Canvas?.getDocument()?.querySelectorAll("[data-tb-style]") ?: return
        for (i in 0 until (nodes.length as Int)) {
            val style = nodes[i].getAttribute("data-tb-style") as String
            if ("{{" !in style) nodes[i].style.cssText = style
        }
    }

    private fun pageWidth() = TemplateHtml.pageWidthPx(output.format as String, output.orientation as String)

    private fun codeArea() = element("tb-code") as HTMLTextAreaElement

    /**
     * The whole template as it stands. [parts] keeps the template's own CSS; in Visual mode the body comes from the
     * editor (section tags back to Mustache) and the editor's style rules are added after the template's CSS.
     */
    private fun currentHtml(): String {
        val ed = editor
        if (!visual) parts = TemplateHtml.split(codeArea().value)
        val body = if (visual && ed != null) TemplateHtml.unprotect((ed.getHtml() as String).replace(Regex("^\\s*<body[^>]*>|</body>\\s*$"), "")) else parts.body
        val titled = TemplateHtml.Parts(parts.title.ifBlank { input("tb-name") }, parts.headExtra, parts.css, body)
        return TemplateHtml.join(titled, if (visual && ed != null) ed.getCss() as String else "")
    }

    private fun showCode() {
        val code = codeArea()
        code.value = currentHtml()
        parts = TemplateHtml.split(code.value)
        editor?.destroy(); editor = null
        visual = false
        element("tb-gjs")?.hidden = true; code.hidden = false
        tab("tb-mode-code")
    }

    private fun showVisual() {
        if (visual) return
        val candidate = TemplateHtml.split(codeArea().value)
        TemplateHtml.visualBlocker(candidate.body)?.let { toast(it, true); return }
        parts = candidate
        element("tb-code")?.hidden = true; element("tb-gjs")?.hidden = false
        tab("tb-mode-visual")
        loadGrapes { startEditor() }
    }

    private fun tab(active: String) = listOf("tb-mode-visual", "tb-mode-code").forEach { element(it)?.classList?.toggle("active", it == active) }

    // ---- Elements ----

    private fun block(id: String, label: String, category: String, icon: String, html: String) =
        json("id" to id, "label" to "<span class=\"material-symbols-rounded\">$icon</span><div>${escape(label)}</div>", "category" to category,
            "content" to TemplateHtml.protect(html))

    private fun blocks(): Array<dynamic> = arrayOf(
        block("tb-header", "Header", "Text", "title", "<div class=\"tb-header\"><div><div class=\"tb-label\">Report</div><h1 class=\"tb-h1\">Title</h1></div><div class=\"tb-label\">Date</div></div>"),
        block("tb-h1", "Heading", "Text", "format_h1", "<h1 class=\"tb-h1\">Heading</h1>"),
        block("tb-h2", "Subheading", "Text", "format_h2", "<h2 class=\"tb-h2\">Subheading</h2>"),
        block("tb-h3", "Small heading", "Text", "format_h3", "<h3 class=\"tb-h3\">Small heading</h3>"),
        block("tb-text", "Paragraph", "Text", "notes", "<p>Write your text here.</p>"),
        block("tb-label", "Label", "Text", "label", "<div class=\"tb-label\">Label</div>"),
        block("tb-badge", "Badge", "Text", "sell", "<span class=\"tb-badge\">Status</span>"),
        block("tb-field", "Text field", "Form", "text_fields", "<div class=\"tb-field\"><span>Name:</span><i></i></div>"),
        block("tb-box", "Text box", "Form", "edit_note", "<div class=\"tb-box\"><div class=\"tb-label\">Notes</div></div>"),
        block("tb-check", "Checkbox", "Form", "check_box_outline_blank", "<div class=\"tb-check\"><b></b><span>Item to check</span></div>"),
        block("tb-checklist", "Checklist", "Form", "checklist", "<div class=\"tb-section\"><h3 class=\"tb-h3\">Checklist</h3>" +
            (1..3).joinToString("") { "<div class=\"tb-check\"><b></b><span>Item $it</span></div>" } + "</div>"),
        block("tb-yesno", "Yes / No", "Form", "rule", "<div class=\"tb-check\"><span>Question?</span><b></b><span>Yes</span><b></b><span>No</span></div>"),
        block("tb-date", "Date field", "Form", "event", "<div class=\"tb-field\"><span>Date:</span><i></i></div>"),
        block("tb-sign", "Signature", "Form", "signature", "<div class=\"tb-sign\">Signature</div>"),
        block("tb-section", "Section", "Layout", "check_box_outline_blank", "<div class=\"tb-section\"><h3 class=\"tb-h3\">Section</h3><p>Content</p></div>"),
        block("tb-cols", "2 columns", "Layout", "view_column_2", "<div class=\"tb-cols\"><div><p>Left</p></div><div><p>Right</p></div></div>"),
        block("tb-cols3", "3 columns", "Layout", "view_week", "<div class=\"tb-cols3\"><div><p>One</p></div><div><p>Two</p></div><div><p>Three</p></div></div>"),
        block("tb-table", "Table", "Layout", "table", "<table class=\"tb-table\"><thead><tr><th>Column</th><th>Column</th><th>Column</th></tr></thead><tbody>" +
            (1..3).joinToString("") { "<tr><td>Cell</td><td>Cell</td><td>Cell</td></tr>" } + "</tbody></table>"),
        block("tb-divider", "Divider", "Layout", "horizontal_rule", "<hr class=\"tb-divider\">"),
        block("tb-spacer", "Spacer", "Layout", "height", "<div class=\"tb-spacer\"></div>"),
        block("tb-pagebreak", "Page break", "Layout", "insert_page_break", "<div class=\"tb-pagebreak\"></div>"),
        json("id" to "tb-image", "label" to "<span class=\"material-symbols-rounded\">image</span><div>Image</div>", "category" to "Layout",
            "content" to json("type" to "image", "classes" to arrayOf("tb-image")), "activate" to true),
        block("tb-each-table", "Repeating table", "Data", "table_rows", "<table class=\"tb-table\"><thead><tr><th>Item</th><th>Value</th></tr></thead>" +
            "<tbody>{{#rows}}<tr><td>{{item}}</td><td>{{value}}</td></tr>{{/rows}}{{^rows}}<tr><td colspan=\"2\">No records.</td></tr>{{/rows}}</tbody></table>"),
        block("tb-each-check", "Repeating checklist", "Data", "fact_check", "<div>{{#items}}<div class=\"tb-check\"><b>{{#done}}✓{{/done}}</b><span>{{label}}</span></div>{{/items}}</div>"),
        block("tb-if", "Show if", "Data", "visibility", "<div>{{#flag}}<p>Shown when flag is set.</p>{{/flag}}</div>"),
    )

    /** One block per value in the sample data ({{a.b}}), and a repeating block per list. */
    private var dataBlockCount = 0

    private fun dataBlocks() {
        val manager = editor?.Blocks ?: return
        repeat(dataBlockCount) { manager.remove("tb-data-$it") }
        val paths = paths(sample, "")
        dataBlockCount = paths.size
        paths.forEachIndexed { i, path ->
            val html = if (path.endsWith("[]")) path.removeSuffix("[]").let { "<div>{{#$it}}<p>{{.}}</p>{{/$it}}</div>" }
                else "<div class=\"tb-value\"><span class=\"tb-label\">${escape(path.substringAfterLast('.'))}</span><span>{{$path}}</span></div>"
            manager.add("tb-data-$i", block("tb-data-$i", path, "Data fields", if (path.endsWith("[]")) "list" else "data_object", html))
        }
    }

    private fun paths(value: dynamic, prefix: String): List<String> {
        if (value == null || jsTypeOf(value) != "object") return emptyList()
        return (js("Object").keys(value) as Array<String>).flatMap { key ->
            val path = if (prefix.isEmpty()) key else "$prefix.$key"
            val child = value[key]
            when {
                js("Array").isArray(child) as Boolean -> listOf("$path[]")
                child != null && jsTypeOf(child) == "object" -> paths(child, path)
                else -> listOf(path)
            }
        }
    }

    // ---- Page, sample data, preview, save ----

    private fun pageSettings() {
        val format = output.format as String
        val orientation = output.orientation as String
        modal("Page", """
          <div class="field"><label for="tb-format">Size</label><select id="tb-format">${listOf("A4", "A3", "Letter").joinToString("") { "<option${if (it == format) " selected" else ""}>$it</option>" }}</select></div>
          <div class="field"><label for="tb-orientation">Orientation</label><select id="tb-orientation">${listOf("portrait", "landscape").joinToString("") { "<option value=\"$it\"${if (it == orientation) " selected" else ""}>${it.replaceFirstChar(Char::uppercase)}</option>" }}</select></div>
          <label class="tb-toggle"><input type="checkbox" id="tb-pagenumbers"${if (output.pageNumbers == true) " checked" else ""}> Page numbers ("Page x of y")</label>
          <div class="field"><label for="tb-letterhead">Letterhead label</label><input id="tb-letterhead" class="input" maxlength="40" value="${escape(output.letterhead)}" placeholder="Blank for no letterhead">
            <p class="field-help">Repeats the Zora Innovations logo with this label at the top of every page. Leave about 24mm of top margin.</p></div>
        """.trimIndent(), "<button id=\"tb-page-done\" class=\"btn btn-primary\">Done</button>")
        click("tb-page-done") {
            if (!visual) parts = TemplateHtml.split(codeArea().value)
            output.format = select("tb-format"); output.orientation = select("tb-orientation")
            output.pageNumbers = (element("tb-pagenumbers") as? org.w3c.dom.HTMLInputElement)?.checked == true
            output.letterhead = input("tb-letterhead")
            parts = TemplateHtml.Parts(parts.title, parts.headExtra, TemplateHtml.withPageSize(parts.css, output.format as String, output.orientation as String), parts.body)
            dirty = true
            closeModal()
            if (visual) { editor?.Devices?.get("page")?.set("width", "${pageWidth()}px"); editor?.setDevice("page"); canvasCss() }
            else codeArea().value = TemplateHtml.join(parts)
        }
    }

    private fun sampleData() {
        modal("Sample data", """
          <p class="field-help">Example data for previews, one entry per data source: <code>{"trip": {"driver": "Sipho"}}</code> fills <code>{{trip.driver}}</code>.
          Each value appears under Data fields in the builder. A company's real report fills these from its own server.</p>
          <textarea id="tb-sample-json" class="tb-code tb-sample" spellcheck="false" aria-label="Sample data JSON">${escape(js("JSON").stringify(sample, null, 2))}</textarea>
        """.trimIndent(), "<button id=\"tb-sample-done\" class=\"btn btn-primary\">Use this data</button>", wide = true)
        click("tb-sample-done") {
            val text = (element("tb-sample-json") as HTMLTextAreaElement).value
            val parsed: dynamic = try { js("JSON").parse(text.ifBlank { "{}" }) } catch (e: Throwable) { toast("That isn't valid JSON: ${e.message}", true); return@click }
            if (parsed == null || jsTypeOf(parsed) != "object" || js("Array").isArray(parsed) as Boolean) { toast("Sample data must be a JSON object, e.g. {\"data\": {…}}.", true); return@click }
            sample = parsed; dirty = true
            closeModal(); dataBlocks()
        }
    }

    private fun preview() {
        val button = element("tb-preview") as? HTMLButtonElement ?: return
        val request = json("company" to draft.company, "reportId" to draft.reportId, "version" to draft.fromVersion,
            "draft" to json("name" to input("tb-name"), "template" to currentHtml(), "output" to output, "sample" to sample))
        TemplatesScreen.renderPdf(button, request, input("tb-name").ifBlank { "Template" } + " · draft")
    }

    private fun save() {
        val html = currentHtml()
        val next = draft.newest + 1
        modal("Save as v$next", """
          ${if (draft.fromVersion == null) "" else "<p class=\"field-help\">Saves a new version of ${escape(draft.name)}. v${draft.fromVersion} and every other published version stay as they are.</p>"}
          <div class="field"><label for="tb-description">Description</label><textarea id="tb-description" class="input" maxlength="500" rows="2" placeholder="What the report shows">${escape(draft.description)}</textarea></div>
          <div class="field"><label for="tb-notes">What's new in v$next</label><textarea id="tb-notes" class="input" maxlength="1000" rows="3" placeholder="${if (draft.fromVersion == null) "First version." else "What changed from v${draft.fromVersion}"}"></textarea></div>
        """.trimIndent(), "<button id=\"tb-save-confirm\" class=\"btn btn-primary\"><span class=\"material-symbols-rounded\">publish</span> Publish v$next</button>")
        click("tb-save-confirm") {
            val notes = (element("tb-notes") as HTMLTextAreaElement).value.trim()
            if (notes.isBlank()) { toast("Describe what this version changes.", true); return@click }
            val button = element("tb-save-confirm") as HTMLButtonElement
            button.disabled = true; button.textContent = "Publishing…"
            val request = json("company" to draft.company, "reportId" to draft.reportId, "isNew" to (draft.fromVersion == null), "fromVersion" to draft.fromVersion,
                "name" to input("tb-name"), "description" to (element("tb-description") as HTMLTextAreaElement).value.trim(),
                "releaseNotes" to notes, "template" to html, "output" to output, "sample" to sample)
            promise(FirebaseGateway.callable("saveReportTemplate", request), { result ->
                closeModal(); dirty = false
                toast("Published ${input("tb-name")} v${result.data.version}.")
                close()
                saved(draft.company)
            }, { message -> button.disabled = false; button.textContent = "Publish v$next"; toast(message, true) })
        }
    }

    private fun close() {
        if (dirty) {
            confirmModal("Discard changes?", "Your changes to this template haven't been saved.", "tb-discard", "Discard", icon = "delete", cancelLabel = "Keep editing")
            click("tb-discard") { dirty = false; closeModal(); close() }
            return
        }
        editor?.destroy(); editor = null
        element("tb-overlay")?.remove()
        document.body?.classList?.remove("tb-open")
    }
}

/** For the New template dialog: "Trip sheet" → "trip-sheet". */
internal fun templateId(name: String) = name.lowercase().replace(Regex("[^a-z0-9]+"), "-").trim('-').take(80)
